import { defineComponent, reactive, SetupContext, computed, onMounted, ref } from '@vue/composition-api';
import vueRouter from '@/router/index';
import { AttachmentFile } from '@/shared/type';
import RichTextEditor from '../common/rich-text-editor.component.vue';
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';
import HttpStatus from 'http-status-codes';
import SystemConst from '@/shared/system-const';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };                                           // Windowサイズ
    title: string;                                                                  // タイトル
    receiveDate: string;                                                            // 受信日
    sendDate: string;                                                               // 送付日
    sender: string;                                                                 // （発信組織）発信者
    importantInfoMessage: string;                                                   // メッセージ
    attachmentFiles: AttachmentFile[];                                              // 添付ファイル
    isAttachDisplay: boolean;                                                       // 添付ファイル表示切替
}

/**
 * 重要情報のお知らせ詳細画面コンポーネント
 */
export default defineComponent({
    components: { RichTextEditor, CommonMessageDialog },
    setup(props, ctx: SetupContext) {
        // リアクティブな変数定義
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                                             // ウィンドウサイズ
            title: '',                                                              // タイトル
            receiveDate: '',                                                        // 受信日
            sendDate: '',                                                           // 送付日
            sender: '',                                                             // （発信組織）発信者
            importantInfoMessage: '',                                               // メッセージ
            attachmentFiles: [],                                                    // 添付ファイル情報
            isAttachDisplay: false                                                  // 添付ファイル表示切替
        });
        const router = vueRouter;                                                   // ルータ
        const commonRef = ref<any>(null);                                           // メッセージダイアログ
        const importantInfoId = computed(() => ctx.root.$route.params.id).value;    // 重要情報のお知らせ詳細ID
        const sendSeq = computed(() => ctx.root.$route.params.seq).value;           // 重要情報のお知らせシーケンス
        const transition = computed(() => ctx.root.$route.params.transition).value; // 遷移元パラメータ（'receive': 受信一覧, 'send': 送付履歴詳細一覧）
        const fileIconList = SystemConst.FILE_EXTENSION_ICON;                       // 添付ファイル拡張子アイコンリスト
        const detailHeight = SystemConst.DETAIL_HEIGHT.IMPORTANT_INFORMATION;       // 詳細画面高さ

        /**
         * 初期処理
         * （リッチテキストエディタに表示するため、DOMの生成後のライフサイクルであるonMountedを使用）
         */
        onMounted(() => {
            // 遷移元パラメータを判別
            if (transition === 'receive') {
                // 受信一覧の場合
                initReceive();
            } else if (transition === 'send') {
                // 送付履歴詳細一覧の場合
                initSend();
            }
        })

        /**
         * 初期表示
         */
        created();
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-file-document-outline');

            // mainTitle変更
            ctx.root.$store.commit('setMainTitle', '重要情報のお知らせ詳細');
        }

        /**
         * 受信詳細の初期表示取得
         */
        async function initReceive() {
            // 受信の詳細データを取得する
            const response = await ctx.root.$store.dispatch('importantInformation/getReceiveDetail',
                {
                    importantId: importantInfoId,
                    sendSequence: sendSeq
                }
            );

            // API通信のレスポンスが正常に取得できなかった場合
            if (response.status !== HttpStatus.OK) {
                if (response.status === HttpStatus.NOT_FOUND || response.status === HttpStatus.BAD_REQUEST) {
                    let beforeDisplayPath = '*';
                    if (transition === 'receive') {
                        beforeDisplayPath = '/important-information/receive/list'
                    }
                    // 400, 404エラーの場合、エラーメッセージを表示して遷移元の画面に戻る
                    commonRef.value.displayErrorMessageTransitionDialog(response, beforeDisplayPath);
                    return;
                }
                // その他のエラーの場合、エラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // API通信のレスポンスが正常に取得できた場合、表示するデータを設定する
            state.title = response.data.receiveDetail.title;
            state.receiveDate = response.data.receiveDetail.receivingDate;
            state.sender = response.data.receiveDetail.sender;
            // リッチテキストエディタに取得した内容を登録する
            state.importantInfoMessage = response.data.receiveDetail.body;

            // 添付ファイルデータを取得した場合
            const attachmentFilesNumber = response.data.receiveDetail.attachmentFiles.length;
            if (attachmentFilesNumber !== 0) {
                // 添付ファイルデータ設定を行う
                setAttachmentFiles(attachmentFilesNumber, response.data.receiveDetail.attachmentFiles);
            }
        }

        /**
         * 送付履歴詳細の初期表示取得
         */
        async function initSend() {
            // 送付履歴の詳細データを取得する
            // GET /api/important-information/send-history/detail/{importantId}/{sendSequence}
            // パラメータ：重要情報ID
            //            送付シーケンス
            const response = await ctx.root.$store.dispatch('importantInformation/getSendHistoryDetail',
                {
                    importantId: importantInfoId,
                    sendSequence: sendSeq
                }
            );

            // API通信のレスポンスが正常に取得できなかった場合
            if (response.status !== HttpStatus.OK) {
                if (response.status === HttpStatus.NOT_FOUND || response.status === HttpStatus.BAD_REQUEST) {
                    // 400, 404エラーの場合、エラーメッセージを表示して遷移元の画面に戻る
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/important-information/send-history/detail/' + importantInfoId);
                    return;
                }
                // その他のエラーの場合、エラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // API通信のレスポンスが正常に取得できた場合、表示するデータを設定する
            state.title = response.data.sendHistoryDetail.title;
            state.sendDate = response.data.sendHistoryDetail.sendDate;
            // リッチテキストエディタに取得した内容を登録する
            state.importantInfoMessage = response.data.sendHistoryDetail.body;

            // 添付ファイルデータを取得した場合
            const attachmentFilesNumber = response.data.sendHistoryDetail.attachmentFiles.length;
            if (attachmentFilesNumber !== 0) {
                // 添付ファイルデータ設定を行う
                setAttachmentFiles(attachmentFilesNumber, response.data.sendHistoryDetail.attachmentFiles);
            }
        }

        /**
         * 添付ファイルデータ設定
         */
        function setAttachmentFiles(fileCount: number, attachmentFiles: any) {
            // 以下を添付ファイルの数分繰り返す
            for (let i = 0; i < fileCount; i++) {
                const attachFile: AttachmentFile[] = [                  // 一時保存する添付ファイル情報
                    {
                        fileIcon: '',
                        fileSequence: 0,
                        fileName: '',
                        fileSize: ''
                    }
                ];
                attachFile[0].fileSequence = attachmentFiles[i].fileSequence;
                attachFile[0].fileName = attachmentFiles[i].fileName;

                // 添付ファイル名から拡張子部分を取得する
                const extension = attachFile[0].fileName.substring(attachFile[0].fileName.lastIndexOf('.') + 1);
                // アイコンのリストを作成する
                const fileIcon = Object.keys(fileIconList);
                // 添付ファイルアイコンを拡張子から判別し、設定する
                Object.values(fileIconList).forEach((element, index) => {
                    // 添付ファイルの拡張子を小文字に変換して比較する
                    const iconList = element.some((extensionList) => extensionList === extension.toLowerCase());
                    if (iconList === true) {
                        // 拡張子を見つけたときの添え字番号で検索し、設定する
                        attachFile[0].fileIcon = fileIcon[index];
                    }
                    if (attachFile[0].fileIcon === '' && fileIcon.length - 1 === index) {
                        // 最後まで検索し、一致する拡張子がなかった場合
                        attachFile[0].fileIcon = fileIcon[index];
                    }
                });

                // 添付ファイルのサイズを「B(バイト)」表記から「KB(キロバイト)」表記へ変換する
                let attachFileSize = Number(attachmentFiles[i].fileSize);
                attachFileSize = attachFileSize / 1000;
                attachFile[0].fileSize = attachFileSize.toFixed(3);

                state.attachmentFiles.push(attachFile[0]);
            }
            // 添付ファイルの表示切り替える
            state.isAttachDisplay = true;
        }

        /**
         * 添付ファイルダウンロード
         * @param fileSequence ファイルシーケンス
         * @param fileName ダウンロードファイル名
         */
        async function fileDownload(fileSequence: number, fileName: string) {
            // 添付ファイルを取得するAPIを呼び出す
            const response = await ctx.root.$store.dispatch('importantInformation/attachmentDownload', {
                importantId: importantInfoId,
                sendSequence: sendSeq,
                fileSequence: fileSequence,
                transition: transition,
                fileName: fileName
            });

            // API通信成否判定
            if (response.status !== HttpStatus.OK) {
                // 正常でない場合エラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);
            }
            return;
        }

        /**
         * 戻るボタン押下時の処理
         */
        function back() {
            router.back();
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        return {
            state,
            importantInfoId,
            sendSeq,
            transition,
            commonRef,
            detailHeight,
            fileDownload,
            back,
            onResize
        };
    }
});
